const NETWORK_ERROR_MESSAGE =
  'Could not reach Ticketlab. Check your connection and try again.';
const PARSE_ERROR_MESSAGE =
  'Received an unexpected response from the server. Please try again.';
const UNEXPECTED_RESPONSE_MESSAGE =
  'Unexpected response from the server. Please try again.';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const apiErrorModel = {
  AUTH_HTTP_STATUSES: [401, 403, 419],
  AUTH_FAILURE_MESSAGE_PATTERN:
    /unauthenticated|unauthorized|permission(s)? denied|access denied|invalid token|expired token|token (has )?expired|not authenticated|login required/i,
  createRequestId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  },

  extractMessageFromBody(body) {
    if (body == null) {
      return null;
    }

    if (typeof body.message === 'string' && body.message !== '') {
      return body.message;
    }

    if (body.error != null) {
      if (typeof body.error === 'string') {
        return body.error;
      }
      if (typeof body.error.message === 'string' && body.error.message !== '') {
        return body.error.message;
      }
    }

    return null;
  },

  isAuthFailure(status, body) {
    if (apiErrorModel.AUTH_HTTP_STATUSES.includes(status)) {
      return true;
    }

    if (body == null) {
      return false;
    }

    const code = Number(body.code);
    if (code === 401 || code === 403) {
      return true;
    }

    const message = apiErrorModel.extractMessageFromBody(body);
    if (!message) {
      return false;
    }

    return apiErrorModel.AUTH_FAILURE_MESSAGE_PATTERN.test(message);
  },

  isApiFailureBody(body) {
    if (body == null) {
      return false;
    }

    if (body.status === 'error' && body.message) {
      return true;
    }

    const code = Number(body.code);
    return !Number.isNaN(code) && code >= 400 && Boolean(body.message);
  },

  normalizeNetworkError(error, requestId) {
    const rawMessage = error?.message || String(error);
    const isKnownNetworkError =
      rawMessage === 'Load failed' ||
      rawMessage === 'Failed to fetch' ||
      rawMessage === 'Network error' ||
      rawMessage === 'NetworkError when attempting to fetch resource.';

    return {
      message: isKnownNetworkError ? NETWORK_ERROR_MESSAGE : rawMessage || DEFAULT_ERROR_MESSAGE,
      code: 'NETWORK_ERROR',
      requestId,
      retryable: true,
      rawMessage,
    };
  },

  normalizeHttpError(status, body, requestId) {
    const message =
      apiErrorModel.extractMessageFromBody(body) || `Request failed (${status})`;

    return {
      message,
      code: status >= 500 ? 'SERVER_ERROR' : 'HTTP_ERROR',
      status,
      requestId,
      retryable: status >= 500 || status === 429,
    };
  },

  normalizeParseError(requestId) {
    return {
      message: PARSE_ERROR_MESSAGE,
      code: 'PARSE_ERROR',
      requestId,
      retryable: true,
    };
  },

  normalizeUnexpectedResponse(requestId) {
    return {
      message: UNEXPECTED_RESPONSE_MESSAGE,
      code: 'UNEXPECTED_RESPONSE',
      requestId,
      retryable: true,
    };
  },

  normalizeApiFailureBody(body, requestId) {
    return {
      message: apiErrorModel.extractMessageFromBody(body) || DEFAULT_ERROR_MESSAGE,
      code: 'API_ERROR',
      status: body?.code != null ? Number(body.code) : undefined,
      requestId,
      retryable: false,
    };
  },

  wrapError(error) {
    return { error };
  },

  hasError(response) {
    return response != null && response.error != null;
  },

  getDisplayMessage(error) {
    if (error == null) {
      return DEFAULT_ERROR_MESSAGE;
    }

    if (typeof error === 'string') {
      return error;
    }

    const message = error.message || DEFAULT_ERROR_MESSAGE;
    if (error.requestId) {
      return `${message} (ref: ${error.requestId})`;
    }

    return message;
  },
};

export default apiErrorModel;
